import { useMemo, useRef, useState } from 'react';
import { PageInfoType } from '../types/common';
import { SortBy } from 'tea-component/lib/table/addons';

export function usePageInfo({
  defaultPageInfo = { page: 1, page_size: 50 },
}: {
  defaultPageInfo?: PageInfoType;
}) {
  const [pageInfo, setPageInfo] = useState<PageInfoType>({
    ...defaultPageInfo,
  });

  function changePageInfo(pageInfo: PageInfoType = defaultPageInfo) {
    setPageInfo({ ...pageInfo });
  }

  function resetPageInfo() {
    setPageInfo({ ...pageInfo, page: 1 });
  }

  function changePageInfoNoRender(
    pageInfoValue: PageInfoType = defaultPageInfo,
  ) {
    pageInfo.page = pageInfoValue.page;
    pageInfo.page_size = pageInfoValue.page_size;

    setPageInfo(pageInfo);
  }

  function nextPage() {
    setPageInfo(value => {
      return {
        ...value,
        page: value.page + 1,
      };
    });
  }

  function prePage() {
    setPageInfo(value => {
      return {
        ...value,
        page: value.page - 1,
      };
    });
  }

  return {
    pageInfo,
    changePageInfo,
    changePageInfoNoRender,
    nextPage,
    prePage,
    resetPageInfo,
  };
}

export function useTablePropsInfo<T>({
  defaultSortBy = {
    by: 'create_time',
    order: 'desc',
  },
  defaultPageInfo,
  defaultLoading = false,
}: {
  defaultSortBy?: SortBy<{ by: string; order: 'desc' | 'asc' }>;
  defaultPageInfo?: PageInfoType;
  defaultLoading?: boolean;
}) {
  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const {
    pageInfo,
    changePageInfo,
    changePageInfoNoRender,
    nextPage,
    prePage,
    resetPageInfo,
  } = usePageInfo({
    defaultPageInfo,
  });
  const [loading, setLoading] = useState(defaultLoading);
  const [sortBy, setSortBy] = useState<SortBy<any>>(defaultSortBy);

  const sortFilter = useMemo(() => {
    return {
      sort_item: sortBy.by,
      sort_model: sortBy.order,
    };
  }, [sortBy]);

  function changeSortBy(sortBy: SortBy<any>) {
    setSortBy(sortBy);
    resetPageInfo();
  }

  return {
    data,
    setData,
    total,
    setTotal,
    pageInfo,
    changePageInfo,
    changePageInfoNoRender,
    nextPage,
    prePage,
    loading,
    setLoading,
    sortBy,
    setSortBy,
    sortFilter,
    changeSortBy,
    resetPageInfo,
  };
}

type UseStateRef<T> = [
  React.MutableRefObject<T | undefined>,
  T | undefined,
  (newState: T | undefined) => void,
];

/**
 * 支持不传默认值的 useStateRef Hook
 * @param initialValue 可选的初始化值
 * @returns 返回一个包含状态引用、当前状态值和更新状态值函数的数组
 */
export function useStateRef<T>(initialValue?: T): UseStateRef<T> {
  const [value, setValue] = useState<T | undefined>(initialValue);
  const valueRef = useRef<T | undefined>(initialValue);

  function changeValue(newValue: T | undefined) {
    valueRef.current = newValue;
    setValue(newValue);
  }

  return [valueRef, value, changeValue];
}

type UseSyncState<T> = {
  state: T;
  previousState: T;
  setState: React.Dispatch<React.SetStateAction<T>>;
  syncStates: (newState?: T) => void;
  revertToPrevious: () => void;
  initStates: (newState: T) => void;
};

/**
 * 两个状态
 * state 做页面展示，等更改操作，previousState 为保存前一个状态
 * 提供方法 syncStates 同步两个状态
 * 提供方法 revertToPrevious 将A恢复到B
 * 提供方法 initStates 同时同步两个状态
 * @param initialValue
 * @returns
 */
export function useSyncState<T>(initialValue: T): UseSyncState<T> {
  const [state, setState] = useState<T>(initialValue);
  const [previousState, setPreviousState] = useState<T>(initialValue);

  const initStates = (value: T) => {
    setState(value);
    setPreviousState(value);
  };

  const syncStates = (value: T = state) => {
    setPreviousState(value);
  };

  const revertToPrevious = () => {
    setState(previousState);
  };

  return {
    state,
    previousState,
    setState,
    syncStates,
    revertToPrevious,
    initStates,
  };
}

export function useFontLoading(props?: { defaultLoading?: boolean }) {
  const [loading, setLoading] = useState(props?.defaultLoading || false);

  function changeLoading() {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 300);
  }

  return { loading, changeLoading };
}

// 自定义Hook
export function useListRefs<T>(initialLength = 0) {
  const refs = useRef<(T | null)[]>(Array(initialLength).fill(null));

  const setRef = (index: number) => (el: T | null) => {
    refs.current[index] = el;
  };

  const initRefs = (length = 0) => {
    refs.current = Array(length).fill(null);
  };

  const getRef = (index: number) => refs.current[index];

  const getAllRefs = () => refs.current;

  return { setRef, getRef, getAllRefs, initRefs };
}
